import { useCallback, useState } from 'react';
import type { Panel, IndicatorData, LabeledListData } from '@/types/dashboard';
import type { LabeledListConfig } from '@/types/labeled-list';
import type { ChartConfig } from '@/types/chart';
import { t } from '@/i18n';
import { FieldSuggestionsPanel } from './FieldSuggestionsPanel';
import { FormulaFieldFilter } from './FormulaFieldFilter';
import './EditorData.css';

interface EditorDataProps {
  panel: Panel;
  onFormulasChange: (formulas: Record<string, string>) => void;
  sourceKey: string;
}

function FormulaHint({ type }: { type: 'indicator' | 'labeledList' }) {
  const hintKey = `dashboard.formulaHint.${type}`;
  const text = t(hintKey);
  if (text === hintKey) return null;

  return (
    <div
      className="form-text mt-1"
      data-testid={`formula-hint-${type}`}
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
}

export function EditorData({ panel, onFormulasChange, sourceKey }: EditorDataProps) {
  const [showHelp, setShowHelp] = useState(false);

  const handleIndicatorChange = useCallback(
    (value: string) => {
      onFormulasChange({ main: value });
    },
    [onFormulasChange]
  );

  const handleLabeledListChange = useCallback(
    (key: string, value: string, allFormulas: Record<string, string>) => {
      onFormulasChange({ ...allFormulas, [key]: value });
    },
    [onFormulasChange]
  );

  const renderFormulas = () => {
    // Chat has no formula configuration
    if (panel.type === 'chat') return null;

    if (panel.type === 'indicator' || panel.type === 'entity_list') {
      const data = panel.data as IndicatorData | undefined;
      const formula = data?.formulas?.main ?? '';

      return (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.formula')}</label>
          <textarea
            className="form-control"
            data-testid="editor-formula-input"
            rows={3}
            value={formula}
            onChange={(e) => handleIndicatorChange(e.target.value)}
          />
          {panel.type === 'indicator' && <FormulaHint type="indicator" />}
        </div>
      );
    }

    if (panel.type === 'labeled_list') {
      const data = panel.data as LabeledListData | undefined;
      const formulas = data?.formulas ?? {};
      const config = panel.config as unknown as LabeledListConfig;
      const fields = config.fields ?? [];

      const filterItems = fields.map(f => ({
        label: f.label || f.fieldName,
        formula: formulas[f.fieldName] ?? '',
        __fid: f.fieldName,
      }));

      return (
        <div>
          <FormulaFieldFilter
            items={filterItems}
            renderItem={({ __fid }) => {
              const field = fields.find(f => f.fieldName === __fid);
              if (!field) return null;
              return (
                <div key={field.fieldName} className="mb-3">
                  <label className="form-label">{field.label || field.fieldName}</label>
                  <textarea
                    className="form-control"
                    data-testid={`editor-formula-input-${field.fieldName}`}
                    rows={2}
                    value={formulas[field.fieldName] ?? ''}
                    onChange={(e) => handleLabeledListChange(field.fieldName, e.target.value, formulas)}
                  />
                </div>
              );
            }}
          />
          <FormulaHint type="labeledList" />
        </div>
      );
    }

    if (panel.type === 'chart') {
      const config = panel.config as unknown as ChartConfig;
      const series = config.series ?? [];

      if (series.length === 0) {
        return <div className="text-muted">{t('dashboard.widget.chart.noConfig')}</div>;
      }

      const formulas = (panel.data && 'formulas' in panel.data)
        ? (panel.data as { formulas?: Record<string, string> }).formulas ?? {}
        : {};

      const filterItems = series.map(s => ({
        label: s.label || s.fieldName,
        formula: formulas[s.fieldName] ?? '',
        __fid: s.fieldName,
      }));

      return (
        <div>
          <FormulaFieldFilter
            items={filterItems}
            renderItem={({ __fid }) => {
              const s = series.find(ser => ser.fieldName === __fid);
              if (!s) return null;
              return (
                <div key={s.fieldName} className="mb-3">
                  <label className="form-label">{s.label || s.fieldName}</label>
                  <textarea
                    className="form-control"
                    data-testid={`editor-formula-input-${s.fieldName}`}
                    rows={2}
                    value={formulas[s.fieldName] ?? ''}
                    onChange={(e) => handleLabeledListChange(s.fieldName, e.target.value, formulas)}
                  />
                </div>
              );
            }}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="editor-data-container">
      <div className="editor-data-formulas">
        {renderFormulas()}
        <button
          type="button"
          className="editor-data-toggle"
          data-testid="editor-data-toggle-help"
          onClick={() => setShowHelp((prev) => !prev)}
          title={showHelp ? t('dashboard.hideHelp') : t('dashboard.showHelp')}
        >
          <i className={`las ${showHelp ? 'la-times' : 'la-question-circle'}`}></i>
        </button>
      </div>
      <div className={`editor-data-help${showHelp ? '' : ' collapsed'}`} data-testid="editor-data-help-panel">
        <div className="editor-data-help-header">{t('dashboard.help')}</div>
        <div className="editor-data-help-body">
          <FieldSuggestionsPanel sourceKey={sourceKey} />
        </div>
      </div>
    </div>
  );
}
